import { constants, DateWrapper, ProductStatusInfo } from '@trucktrax/trucktrax-common';
import { ProductLine, StatusEventDto } from '@trucktrax/trucktrax-ts-common';

import { determineMarkerPosition, PositionMarkerDataInfo } from './markerUtil';
import { EXTERNAL, ICON_DOT_CLASS, INTERNAL } from '../constants/appConstants';
import { MapMarker, TicketWithCurrentStatusDto } from '../types';
import { StatusFolderItem } from './ticketUtil';
import recordLog, { LogLevel } from './errorUtil';
import { hasIncomingTicketArrived } from './incomingTicketStatusUtil';

// External/integration event the BE emits as a raw string — there's no
// STATUS_IDS constant for it upstream. Surfaces in multiple buckets here
// (IM in-transit, Aggregates ignore, predictive-route eligibility), so
// pinning it once avoids drift between call sites.
const STATUS_DELIVERY_START = 'DeliveryStart';

export interface Folder<T> {
  id: string;
  name: string;
  dataTest?: string;
  color: string;
  icon: string;
  text?: string;
  dateText?: string;
  timeText?: string;
  itemList: T[];
  key: string;
}

export type FolderItem = MapMarker & StatusFolderItem;

export default function sortStatuses(
  statusA: StatusEventDto | null,
  statusB: StatusEventDto | null,
) {
  if (statusA === null || statusA.timestamp === null || statusA.timestamp === undefined) {
    return 1;
  }
  if (statusB === null || statusB.timestamp === null || statusB.timestamp === undefined) {
    return -1;
  }
  if (statusA.timestamp === statusB.timestamp) {
    return 0;
  }

  // sort oldest to newest
  return new DateWrapper(statusB.timestamp).isAfter(new Date(statusA.timestamp)) ? -1 : 1;
}

export function aggFolderList<T extends FolderItem>(
  listType: string,
  source: FolderListSource,
): Folder<T>[] {
  const folderList = createFolderListForWorkflowProductLines<T>(listType, source, 'Aggregates');
  return folderList;
}

export function cementFolderList<T extends FolderItem>(
  listType: string,
  source: FolderListSource,
): Folder<T>[] {
  const folderList = createFolderListForWorkflowProductLines<T>(listType, source, 'Cement');
  return folderList;
}

export function readyMixFolderList<T extends FolderItem>(
  listType: string,
  source: FolderListSource,
): Folder<T>[] {
  const folderList = createFolderListForWorkflowProductLines<T>(listType, source, 'ReadyMix');
  return folderList;
}

function createFolderListForWorkflowProductLines<T>(
  listType: string,
  source: FolderListSource,
  productLine: WorkFlowProductLines,
): Folder<T>[] {
  let workflow: ReadonlyArray<StatusInfo> = [];
  switch (source) {
    case FOLDER_LIST_SOURCE_KEY.INTERNAL_TRUCKS:
      workflow = statusWorkflowByProductLineInternal[productLine];
      break;
    case FOLDER_LIST_SOURCE_KEY.INTERNAL_DRIVER_ROUTES:
      workflow = statusWorkflowByProductLineInternalRouteInfo[productLine];
      break;
    case FOLDER_LIST_SOURCE_KEY.EXTERNAL_TRUCKS:
      workflow = statusWorkflowByProductLineExternal[productLine];
      break;
    case FOLDER_LIST_SOURCE_KEY.EXTERNAL_DRIVER_ROUTES:
      workflow = statusWorkflowByProductLineExternalRouteInfo[productLine];
      break;
    default:
      workflow = statusWorkflowByProductLineInternal[productLine];
      break;
  }

  const folderList = convertWorkflowToStatusFolders<T>(workflow, source, listType);
  return folderList;
}

export function mapAggTrucksForFolderList<T extends FolderItem>(
  trucks: T[],
  source: FolderListSource,
) {
  const folderList = aggFolderList(constants.TRUCKS_STRING, source);
  const updatedFolders = addItemsToFolders(trucks, source, folderList, getAggregatesFolderKey);
  return updatedFolders;
}

export function mapCementTrucksForFolderList<T extends FolderItem>(
  trucks: T[],
  source: FolderListSource,
) {
  const folderList = cementFolderList(constants.TRUCKS_STRING, source);
  const updatedFolders = addItemsToFolders(trucks, source, folderList, getCementFolderKey);
  return updatedFolders;
}

export function mapReadyMixTrucksForFolderList<T extends FolderItem>(
  trucks: T[],
  source: FolderListSource,
) {
  const folderList = readyMixFolderList(constants.TRUCKS_STRING, source);
  const updatedFolders = addItemsToFolders(trucks, source, folderList, getReadyMixFolderKey);
  return updatedFolders;
}

function addItemsToFolders<T extends FolderItem>(
  trucks: T[],
  source: FolderListSource,
  folders: Folder<FolderItem>[],
  folderKeySearchFn: FolderKeySearchFn,
): Folder<FolderItem>[] {
  trucks.forEach(truck => {
    const {
      data: { status = constants.STATUS_IDS.UNKNOWN },
    } = truck;

    const folderKey = folderKeySearchFn(status, source);
    if (!folderKey) {
      return;
    }

    const targetFolder = folders.find(folder => folder.key === folderKey);
    targetFolder?.itemList.push(truck);
  });

  return folders;
}

export function hasWorkflowFolderMatch(
  productLine: WorkFlowProductLines,
  status: string,
  source: FolderListSource,
): boolean {
  const match = determineWorkflowFolderKey(productLine, status, source);
  return match != null;
}

export function determineWorkflowFolderKey(
  productLine: WorkFlowProductLines,
  status: string,
  source: FolderListSource,
): string[] | string | undefined {
  switch (productLine) {
    case ProductLine.Aggregates:
      return getAggregatesFolderKey(status, source);
    case ProductLine.Cement:
      return getCementFolderKey(status, source);
    case ProductLine.ReadyMix:
      return getReadyMixFolderKey(status, source);
    case ProductLine.None:
      return undefined;
    default:
      break;
  }

  return undefined;
}

type FolderKeySearchFn = (status: string, source: FolderListSource) => string | undefined;

/**
 * Buckets a raw status string into one of the incoming-materials workflow
 * folders, or returns undefined for statuses the receiver-facing sidebar
 * shouldn't represent at all (genuinely unknown future statuses).
 *
 * Used by the IM markers selector (status overlay for marker color/arrow) —
 * PositionMarker.tsx keys color/icon lookups off these exact one-to-one
 * STATUS_IDS values, so this must keep returning them one-to-one, never
 * collapsed. For the IM sidebar's normalized 3-folder menu, see
 * getIncomingMaterialsSidebarFolderKey below.
 *
 * Mapping rules — receiver-facing semantics, not source-plant semantics:
 *  - Pre-departure (InQueue, Loading, Ticketed) → Ticketed: the truck is
 *    assigned to the order but hasn't started delivery yet.
 *  - In-transit toward destination (DeliveryStart, ToJob) → ToJob.
 *  - At destination (OnJob, UnloadStart, Unloaded) → mapped one-to-one.
 *  - Post-delivery (Returning, InYard) → Unloaded: from the receiver's POV
 *    the delivery is complete. The IM views never see these statuses —
 *    incomingTicketsSelector drops the ticket once the truck leaves
 *    (DEV-4997). Arrival is decided by getIncomingStatusPhase, not this mapping.
 */
export function getIncomingMaterialsFolderKey(status: string): string | undefined {
  switch (status) {
    // Pre-departure: truck assigned but hasn't left the source plant.
    case constants.STATUS_IDS.IN_QUEUE:
    case constants.STATUS_IDS.LOADING:
    case constants.STATUS_IDS.TICKETED:
      return constants.STATUS_IDS.TICKETED;
    // In-transit: DeliveryStart is the external/integration event that
    // fires when a truck begins delivery; for IM it carries the same
    // meaning as ToJob.
    case STATUS_DELIVERY_START:
    case constants.STATUS_IDS.TO_JOB:
      return constants.STATUS_IDS.TO_JOB;
    case constants.STATUS_IDS.ON_JOB:
      return constants.STATUS_IDS.ON_JOB;
    case constants.STATUS_IDS.UNLOAD_START:
      return constants.STATUS_IDS.UNLOAD_START;
    // Post-delivery: from the destination's POV the delivery is complete.
    case constants.STATUS_IDS.UNLOADED:
    case constants.STATUS_IDS.RETURNING:
    case constants.STATUS_IDS.IN_YARD:
      return constants.STATUS_IDS.UNLOADED;
    default:
      return undefined;
  }
}

/**
 * DEV-4157: buckets a raw status string into the IM sidebar's normalized
 * 3 folders (Ticketed / To Plant / At Plant) for the status-folder menu.
 * Distinct from getIncomingMaterialsFolderKey: OnJob/UnloadStart/Unloaded
 * collapse into the At Plant bucket (keyed by ON_JOB) here, but that
 * function's one-to-one mapping still drives map marker color/icon, which
 * has no awareness of this collapsed bucketing.
 *
 * InQueue, Returning, and InYard are intentionally unbucketed:
 *  - InQueue shouldn't be reachable here at all — destinationPlant (what
 *    makes a ticket "incoming") is set at ticket creation, which happens at
 *    or after Ticketed, so a ticket landing in the catch-all "Other" folder
 *    at InQueue flags a data anomaly rather than a normal pre-departure
 *    state worth normalizing away.
 *  - Returning/InYard: the truck has left the destination plant, so
 *    incomingTicketsSelector drops the ticket (DEV-4997) before it reaches
 *    the sidebar.
 */
export function getIncomingMaterialsSidebarFolderKey(status: string): string | undefined {
  switch (status) {
    case constants.STATUS_IDS.LOADING:
    case constants.STATUS_IDS.TICKETED:
      return constants.STATUS_IDS.TICKETED;
    case STATUS_DELIVERY_START:
    case constants.STATUS_IDS.TO_JOB:
      return constants.STATUS_IDS.TO_JOB;
    case constants.STATUS_IDS.ON_JOB:
    case constants.STATUS_IDS.UNLOAD_START:
    case constants.STATUS_IDS.UNLOADED:
      return constants.STATUS_IDS.ON_JOB;
    default:
      return undefined;
  }
}

/**
 * The pure arrival rules are shared with the reducer so status ties cannot
 * change eligibility differently from the Received column's action gate.
 */
// Keep the existing import surface while sharing the pure phase rules with
// the incoming reducer, without pulling this module's service imports into it.
export { hasIncomingTicketArrived } from './incomingTicketStatusUtil';

/**
 * Statuses where the truck has left the destination plant (DEV-4997).
 * Aggregates tickets for internal drivers can stay open through these, so the BE
 * never sends the IM close event; the IM views must drop them on status alone.
 */
const LEFT_DESTINATION_STATUSES = new Set<string>([
  constants.STATUS_IDS.RETURNING,
  constants.STATUS_IDS.IN_YARD,
]);

/**
 * True while a ticket is incoming: it has a destination plant (its delivery
 * location falls inside a plant's geofence) and its truck hasn't left that
 * plant (DEV-4997). Departure (Returning or InYard) ends it whether or not
 * the material was marked received. Reads `currentStatus.status`, never
 * `externalInfo.status`.
 */
export function isIncomingTicket(ticket: TicketWithCurrentStatusDto): boolean {
  if (!ticket.destinationPlant) return false;
  const status = ticket.currentStatus?.status;
  return !status || !LEFT_DESTINATION_STATUSES.has(String(status));
}

export function canAcknowledgeMaterialReceipt(
  ticket: TicketWithCurrentStatusDto,
  hasEditAccess: boolean,
): boolean {
  return (
    !ticket.materialReceivedAt &&
    hasEditAccess &&
    !ticket.voided &&
    hasIncomingTicketArrived(ticket)
  );
}

export function getAggregatesFolderKey(
  status: string,
  source: FolderListSource,
): string | undefined {
  const { isExternalTrucksFolder, canIncludeUnknown } = getFolderVisibility(source);
  const ignoredStatuses = [STATUS_DELIVERY_START];
  const canDefaultToUnknown = canIncludeUnknown && !ignoredStatuses.includes(status);
  switch (status) {
    case constants.STATUS_IDS.IN_QUEUE:
      return constants.STATUS_IDS.IN_QUEUE;
    // exception (Loading is rendered as Ticketed for Agg)
    case constants.STATUS_IDS.LOADING:
    case constants.STATUS_IDS.TICKETED:
      return constants.STATUS_IDS.TICKETED;
    case constants.STATUS_IDS.TO_JOB:
      return constants.STATUS_IDS.TO_JOB;
    case constants.STATUS_IDS.ON_JOB:
      return constants.STATUS_IDS.ON_JOB;
    case constants.STATUS_IDS.UNLOADED:
      if (!isExternalTrucksFolder) {
        return constants.STATUS_IDS.UNLOADED;
      }
      return undefined;
    case constants.STATUS_IDS.RETURNING:
      if (!isExternalTrucksFolder) {
        return constants.STATUS_IDS.RETURNING;
      }
      return undefined;
    case constants.STATUS_IDS.IN_YARD:
      return constants.STATUS_IDS.IN_YARD;
    case constants.STATUS_IDS.UNKNOWN:
      return constants.STATUS_IDS.UNKNOWN;
    default:
      if (canDefaultToUnknown) {
        return constants.STATUS_IDS.UNKNOWN;
      }
  }

  return undefined;
}

export function getCementFolderKey(status: string, source: FolderListSource): string | undefined {
  const { isExternalTrucksFolder, canIncludeUnknown } = getFolderVisibility(source);

  switch (status) {
    case constants.STATUS_IDS.IN_QUEUE:
      return constants.STATUS_IDS.IN_QUEUE;
    // exception (Loading is rendered as Ticketed for Cement)
    case constants.STATUS_IDS.LOADING:
    case constants.STATUS_IDS.TICKETED:
      return constants.STATUS_IDS.TICKETED;
    case constants.STATUS_IDS.TO_JOB:
      return constants.STATUS_IDS.TO_JOB;
    case constants.STATUS_IDS.ON_JOB:
      return constants.STATUS_IDS.ON_JOB;
    case constants.STATUS_IDS.UNLOAD_START:
      if (!isExternalTrucksFolder) {
        return constants.STATUS_IDS.UNLOAD_START;
      }
      return undefined;
    case constants.STATUS_IDS.UNLOADED:
      if (!isExternalTrucksFolder) {
        return constants.STATUS_IDS.UNLOADED;
      }
      return undefined;
    case constants.STATUS_IDS.RETURNING:
      if (!isExternalTrucksFolder) {
        return constants.STATUS_IDS.RETURNING;
      }
      return undefined;
    case constants.STATUS_IDS.IN_YARD:
      return constants.STATUS_IDS.IN_YARD;
    // Unknown, null or other will go to Unknown
    case constants.STATUS_IDS.UNKNOWN:
    default:
      if (canIncludeUnknown) {
        return constants.STATUS_IDS.UNKNOWN;
      }
  }

  return undefined;
}

export function getReadyMixFolderKey(status: string, source: FolderListSource): string | undefined {
  const { isExternalTrucksFolder, canIncludeUnknown } = getFolderVisibility(source);

  switch (status) {
    case constants.STATUS_IDS.IN_QUEUE:
      return constants.STATUS_IDS.IN_QUEUE;
    case constants.STATUS_IDS.TICKETED:
      return constants.STATUS_IDS.TICKETED;
    case constants.STATUS_IDS.LOADING:
      return constants.STATUS_IDS.LOADING;
    case constants.STATUS_IDS.TO_JOB:
      return constants.STATUS_IDS.TO_JOB;
    case constants.STATUS_IDS.ON_JOB:
      return constants.STATUS_IDS.ON_JOB;
    case constants.STATUS_IDS.UNLOAD_START:
      if (!isExternalTrucksFolder) {
        return constants.STATUS_IDS.UNLOAD_START;
      }
      return undefined;
    case constants.STATUS_IDS.UNLOADED:
      if (!isExternalTrucksFolder) {
        return constants.STATUS_IDS.UNLOADED;
      }
      return undefined;
    case constants.STATUS_IDS.RETURNING:
      if (!isExternalTrucksFolder) {
        return constants.STATUS_IDS.RETURNING;
      }
      return undefined;
    case constants.STATUS_IDS.IN_YARD:
      return constants.STATUS_IDS.IN_YARD;
    // Unknown, null or other will go to Unknown
    case constants.STATUS_IDS.UNKNOWN:
    default:
      if (canIncludeUnknown) {
        return constants.STATUS_IDS.UNKNOWN;
      }
  }
  return undefined;
}

function getFolderVisibility(source: FolderListSource): {
  isExternalTrucksFolder: boolean;
  canIncludeUnknown: boolean;
} {
  const isExternalTrucksFolder = source === FOLDER_LIST_SOURCE_KEY.EXTERNAL_TRUCKS;
  const canIncludeUnknown =
    source !== FOLDER_LIST_SOURCE_KEY.EXTERNAL_DRIVER_ROUTES && !isExternalTrucksFolder;
  return { isExternalTrucksFolder, canIncludeUnknown };
}

export type ProductLineStatusMapKeys = keyof typeof constants.STATUS_IDS;
export type ProductLineStatusNameMap = { [key in ProductLineStatusMapKeys]?: string };
export function retrieveProductLineNameDiff(
  selectedProductLine: ProductLine,
): ProductLineStatusNameMap {
  const mutualStatuses = {
    [constants.STATUS_IDS.TICKETED]: constants.STATUS_NAMES.Ticketed,
    [constants.STATUS_IDS.RETURNING]: constants.STATUS_NAMES.Returning,
    [constants.STATUS_IDS.UNKNOWN]: constants.STATUS_NAMES.Unknown,
  };
  const productLineNameMap: { [key: string]: ProductLineStatusNameMap } = {
    [constants.CEMENT_VALUE]: {
      ...mutualStatuses,
      [constants.STATUS_IDS.IN_QUEUE]: constants.STATUS_NAMES.InQueue,
      [constants.STATUS_IDS.TO_JOB]: constants.STATUS_NAMES.ToPlant,
      [constants.STATUS_IDS.ON_JOB]: constants.STATUS_NAMES.AtPlant,
      [constants.STATUS_IDS.UNLOAD_START]: constants.STATUS_NAMES.UnloadStart,
      [constants.STATUS_IDS.UNLOADED]: constants.STATUS_NAMES.UnloadEnd,
      [constants.STATUS_IDS.IN_YARD]: constants.STATUS_NAMES.AtTerminal,
      // exception (Loading is rendered as Ticketed for Cement)
      [constants.STATUS_IDS.LOADING]: constants.STATUS_NAMES.Ticketed,
    },
    // Aggregates have no re-used fields
    [constants.AGGREGATES_VALUE]: {
      ...mutualStatuses,
      [constants.STATUS_IDS.IN_QUEUE]: constants.STATUS_NAMES.InQueue,
      [constants.STATUS_IDS.TO_JOB]: constants.STATUS_NAMES.ToJob,
      [constants.STATUS_IDS.ON_JOB]: constants.STATUS_NAMES.OnJob,
      [constants.STATUS_IDS.UNLOADED]: constants.STATUS_NAMES.Unloaded,
      [constants.STATUS_IDS.IN_YARD]: constants.STATUS_NAMES.InYard,
      // exception (Loading is rendered as Ticketed for Agg)
      [constants.STATUS_IDS.LOADING]: constants.STATUS_NAMES.Ticketed,
    },
    [constants.READY_MIX_VALUE]: {
      ...mutualStatuses,
      [constants.STATUS_IDS.IN_QUEUE]: constants.STATUS_NAMES.ReadyToLoad,
      [constants.STATUS_IDS.LOADING]: constants.STATUS_NAMES.Loaded,
      [constants.STATUS_IDS.TO_JOB]: constants.STATUS_NAMES.ToJob,
      [constants.STATUS_IDS.ON_JOB]: constants.STATUS_NAMES.OnJob,
      [constants.STATUS_IDS.UNLOAD_START]: constants.STATUS_NAMES.Pouring,
      [constants.STATUS_IDS.UNLOADED]: constants.STATUS_NAMES.EndPour,
      [constants.STATUS_IDS.IN_YARD]: constants.STATUS_NAMES.InPlant,
    },
  };
  return productLineNameMap[selectedProductLine];
}

export function getStatusNameFromMapView(position: PositionMarkerDataInfo) {
  const productLine = position?.productLine ?? ProductLine.None;
  const productLineStatuses = retrieveProductLineNameDiff(productLine as ProductLine);
  const status = (position?.status ?? constants.STATUS_IDS.UNKNOWN) as ProductLineStatusMapKeys;

  return productLineStatuses && productLineStatuses[status]
    ? productLineStatuses[status]
    : constants.STATUS_NAMES.Unknown;
}

const EMPTY_STATUS_NAME_MAP: ProductLineStatusNameMap = Object.freeze({});
const markerStatusMapByProductLine = new Map<ProductLine, ProductLineStatusNameMap>();

/**
 * DEV-3924: status-name map for an Incoming Materials marker, keyed to the paired
 * ticket's origination-plant product line (`externalInfo.plantProductLine`) — never the
 * main map's selected product line, and never `position.productLine` (the truck's primary
 * product line, which can disagree with the plant). When unresolved (`None`/missing),
 * returns an empty map so the marker shows no status label rather than a wrong one.
 * Results are cached per product line — this runs for every marker on every render.
 */
export function incomingMarkerStatusMap(position?: {
  ticket?: { externalInfo?: { plantProductLine?: ProductLine } };
}): ProductLineStatusNameMap {
  const ticketProductLine = position?.ticket?.externalInfo?.plantProductLine;
  if (!ticketProductLine || ticketProductLine === ProductLine.None) {
    return EMPTY_STATUS_NAME_MAP;
  }
  let statusNameMap = markerStatusMapByProductLine.get(ticketProductLine);
  if (!statusNameMap) {
    statusNameMap = retrieveProductLineNameDiff(ticketProductLine) ?? EMPTY_STATUS_NAME_MAP;
    markerStatusMapByProductLine.set(ticketProductLine, statusNameMap);
  }
  return statusNameMap;
}

export function getDriversFolderStructureMap() {
  return {
    'external-drivers-folder': EXTERNAL,
    'internal-drivers-folder': INTERNAL,
    External: EXTERNAL,
    Internal: INTERNAL,
  };
}

export const FOLDER_LIST_SOURCE_KEY = {
  INTERNAL_TRUCKS: 'InternalTrucks',
  EXTERNAL_TRUCKS: 'ExternalTrucks',
  INTERNAL_DRIVER_ROUTES: 'InternalRoutes',
  EXTERNAL_DRIVER_ROUTES: 'ExternalDriverRoutes',
} as const;
export type FolderListSource = (typeof FOLDER_LIST_SOURCE_KEY)[keyof typeof FOLDER_LIST_SOURCE_KEY];

export interface StatusInfo {
  label: string;
  color: string;
  dataTestIdPrefix: string;
  key: string;
}

const inYardStatus: StatusInfo = {
  label: constants.STATUS_NAMES.InYard,
  color: constants.STATUS_COLORS.InYard,
  dataTestIdPrefix: 'in-yard',
  key: constants.STATUS_IDS.IN_YARD,
};

const loadedStatus: StatusInfo = {
  label: constants.STATUS_NAMES.Loaded,
  color: constants.STATUS_COLORS.Loading,
  dataTestIdPrefix: 'loaded',
  key: constants.STATUS_IDS.LOADING,
};

const ticketedStatus = {
  label: constants.STATUS_NAMES.Ticketed,
  color: constants.STATUS_COLORS.Ticketed,
  dataTestIdPrefix: 'ticketed',
  key: constants.STATUS_IDS.TICKETED,
};

const toJobStatus = {
  label: constants.STATUS_NAMES.ToJob,
  color: constants.STATUS_COLORS.ToJob,
  dataTestIdPrefix: 'to-job',
  key: constants.STATUS_IDS.TO_JOB,
};

const onJobStatus = {
  label: constants.STATUS_NAMES.OnJob,
  color: constants.STATUS_COLORS.OnJob,
  dataTestIdPrefix: 'on-job',
  key: constants.STATUS_IDS.ON_JOB,
};

const unloadStartStatus = {
  label: constants.STATUS_NAMES.UnloadStart,
  color: constants.STATUS_COLORS.UnloadStart,
  dataTestIdPrefix: 'unload-start',
  key: constants.STATUS_IDS.UNLOAD_START,
};

const unloadedStatus = {
  label: constants.STATUS_NAMES.Unloaded,
  color: constants.STATUS_COLORS.Unloaded,
  dataTestIdPrefix: 'unload-end',
  key: constants.STATUS_IDS.UNLOADED,
};

const returningStatus = {
  label: constants.STATUS_NAMES.Returning,
  color: constants.STATUS_COLORS.Returning,
  dataTestIdPrefix: 'returning',
  key: constants.STATUS_IDS.RETURNING,
};

const inQueueStatus = {
  label: constants.STATUS_NAMES.InQueue,
  color: constants.STATUS_COLORS.InQueue,
  dataTestIdPrefix: 'in-queue',
  key: constants.STATUS_IDS.IN_QUEUE,
};

const unknownStatus = {
  label: constants.STATUS_NAMES.Unknown,
  color: constants.STATUS_COLORS.Unknown,
  dataTestIdPrefix: 'unknown',
  key: constants.STATUS_IDS.UNKNOWN,
};

const readyToLoadStatus = { ...inQueueStatus, label: constants.STATUS_NAMES.ReadyToLoad };
const pouringStatus = { ...unloadStartStatus, label: constants.STATUS_NAMES.Pouring };
const endPourStatus = { ...unloadedStatus, label: constants.STATUS_NAMES.EndPour };
const unloadEndStatus = { ...unloadedStatus, label: constants.STATUS_NAMES.UnloadEnd };

const cementStatusWorkflowInternal: ReadonlyArray<StatusInfo> = [
  inQueueStatus,
  ticketedStatus,
  toJobStatus,
  onJobStatus,
  unloadStartStatus,
  unloadEndStatus,
  returningStatus,
  inYardStatus,
  unknownStatus,
];

const cementStatusWorkflowExternal: ReadonlyArray<StatusInfo> = [
  inYardStatus,
  inQueueStatus,
  ticketedStatus,
  toJobStatus,
  onJobStatus,
];

const cementStatusWorkflowRouteInfoExternal: ReadonlyArray<StatusInfo> = [
  ...cementStatusWorkflowExternal,
  returningStatus,
];

const cementStatusWorkflowRouteInfoInternal: ReadonlyArray<StatusInfo> = [
  ticketedStatus,
  toJobStatus,
  onJobStatus,
  unloadStartStatus,
  unloadEndStatus,
  returningStatus,
  inYardStatus,
  unknownStatus,
];

const readyMixStatusWorkflowInternal: ReadonlyArray<StatusInfo> = [
  readyToLoadStatus,
  ticketedStatus,
  loadedStatus,
  toJobStatus,
  onJobStatus,
  pouringStatus,
  endPourStatus,
  returningStatus,
  inYardStatus,
  unknownStatus,
];

const readyMixStatusWorkflowExternal: ReadonlyArray<StatusInfo> = [
  inYardStatus,
  readyToLoadStatus,
  ticketedStatus,
  toJobStatus,
  onJobStatus,
];

const readyMixStatusWorkflowRouteInfoExternal: ReadonlyArray<StatusInfo> = [
  ...readyMixStatusWorkflowExternal,
  returningStatus,
];

const readyMixStatusWorkflowRouteInfoInternal: ReadonlyArray<StatusInfo> = [
  ticketedStatus,
  loadedStatus,
  toJobStatus,
  onJobStatus,
  pouringStatus,
  endPourStatus,
  returningStatus,
  inYardStatus,
  unknownStatus,
];

const aggregateStatusWorkflowInternal: ReadonlyArray<StatusInfo> = [
  inQueueStatus,
  ticketedStatus,
  toJobStatus,
  onJobStatus,
  unloadedStatus,
  returningStatus,
  inYardStatus,
  unknownStatus,
];

const aggregateStatusWorkflowExternal: ReadonlyArray<StatusInfo> = [
  inYardStatus,
  inQueueStatus,
  ticketedStatus,
  toJobStatus,
  onJobStatus,
];

const aggregateStatusWorkflowRouteInfoInternal: ReadonlyArray<StatusInfo> = [
  ticketedStatus,
  toJobStatus,
  onJobStatus,
  unloadedStatus,
  returningStatus,
  inYardStatus,
  unknownStatus,
];

const aggregateStatusWorkflowRouteInfoExternal: ReadonlyArray<StatusInfo> = [
  ...aggregateStatusWorkflowExternal,
  returningStatus,
];

export type WorkFlowProductLines = keyof Pick<
  typeof ProductLine,
  'Aggregates' | 'Cement' | 'ReadyMix' | 'None'
>;

const statusWorkflowByProductLineInternal: Record<
  WorkFlowProductLines,
  ReadonlyArray<StatusInfo>
> = {
  [ProductLine.Cement]: cementStatusWorkflowInternal,
  [ProductLine.ReadyMix]: readyMixStatusWorkflowInternal,
  [ProductLine.Aggregates]: aggregateStatusWorkflowInternal,
  [ProductLine.None]: [],
} as const;

const statusWorkflowByProductLineInternalRouteInfo: Record<
  WorkFlowProductLines,
  ReadonlyArray<StatusInfo>
> = {
  [ProductLine.Cement]: cementStatusWorkflowRouteInfoInternal,
  [ProductLine.ReadyMix]: readyMixStatusWorkflowRouteInfoInternal,
  [ProductLine.Aggregates]: aggregateStatusWorkflowRouteInfoInternal,
  [ProductLine.None]: [],
} as const;

const statusWorkflowByProductLineExternalRouteInfo: Record<
  WorkFlowProductLines,
  ReadonlyArray<StatusInfo>
> = {
  [ProductLine.Cement]: cementStatusWorkflowRouteInfoExternal,
  [ProductLine.ReadyMix]: readyMixStatusWorkflowRouteInfoExternal,
  [ProductLine.Aggregates]: aggregateStatusWorkflowRouteInfoExternal,
  [ProductLine.None]: [],
} as const;

const statusWorkflowByProductLineExternal: Record<
  WorkFlowProductLines,
  ReadonlyArray<StatusInfo>
> = {
  [ProductLine.Cement]: cementStatusWorkflowExternal,
  [ProductLine.ReadyMix]: readyMixStatusWorkflowExternal,
  [ProductLine.Aggregates]: aggregateStatusWorkflowExternal,
  [ProductLine.None]: [],
} as const;

/**
 * Which `constants.PRODUCT_STATUSES` set describes each product line. The
 * legend renders `ProductStatusInfo` (it needs the abbreviation and sequence
 * number), while the workflows above are `StatusInfo`; the two are joined on
 * `ProductStatusInfo.id === StatusInfo.key`, both being `STATUS_IDS` values.
 */
const productStatusSetKeyByProductLine: Record<WorkFlowProductLines, string> = {
  [ProductLine.Cement]: 'cementStatuses',
  [ProductLine.ReadyMix]: 'readyMixStatuses',
  [ProductLine.Aggregates]: 'aggregateStatuses',
  [ProductLine.None]: '',
} as const;

/**
 * Returns the legend entries for a product line, limited to the statuses that
 * the selected route workflows actually produce. Route history shows completed
 * routes, so the RouteInfo workflows are the ones that apply. When results hold
 * both internal and external routes the union is returned, so the legend
 * explains every dot on the map and nothing more.
 * @param {WorkFlowProductLines} productLine the product line to describe
 * @param {{ includeInternal: boolean; includeExternal: boolean }} ownership which route workflows are present
 * @returns {ProductStatusInfo[]} legend entries ordered by sequence number
 */
export function getRouteHistoryLegendStatuses(
  productLine: WorkFlowProductLines,
  ownership: { includeInternal: boolean; includeExternal: boolean },
): ProductStatusInfo[] {
  const setKey = productStatusSetKeyByProductLine[productLine];
  const statusSet = setKey ? constants.PRODUCT_STATUSES[setKey] : undefined;
  if (!statusSet) {
    return [];
  }

  const workflowKeys = new Set<string>();
  if (ownership.includeInternal) {
    statusWorkflowByProductLineInternalRouteInfo[productLine].forEach(s => workflowKeys.add(s.key));
  }
  if (ownership.includeExternal) {
    statusWorkflowByProductLineExternalRouteInfo[productLine].forEach(s => workflowKeys.add(s.key));
  }

  return (Object.values(statusSet) as ProductStatusInfo[])
    .filter(psi => workflowKeys.has(psi.id))
    .sort((a, b) => a.sequenceNumber - b.sequenceNumber);
}

function convertWorkflowToStatusFolders<T>(
  workflow: ReadonlyArray<StatusInfo>,
  source: string,
  listType: string,
): Folder<T>[] {
  const folders = workflow.map((step: StatusInfo) => {
    const folder = {
      id: `${step.key}_${source}`,
      key: step.key,
      name: step.label,
      color: step.color,
      icon: ICON_DOT_CLASS,
      text: '',
      itemList: [] as T[],
      dataTest: `${step.dataTestIdPrefix}-${listType}`,
    } as Folder<T>;
    return folder;
  });
  return folders;
}

function sortStatusesByWorkflow(
  workflow: Readonly<StatusInfo[]>,
  productLine: WorkFlowProductLines,
  statuses: StatusEventDto[],
  folderSource: FolderListSource,
): StatusEventDto[] {
  const orderedData: StatusEventDto[] = [];
  let folderKeySearchFn: FolderKeySearchFn | undefined;
  switch (productLine) {
    case 'Aggregates':
      folderKeySearchFn = getAggregatesFolderKey;
      break;
    case 'Cement':
      folderKeySearchFn = getCementFolderKey;
      break;
    case 'ReadyMix':
      folderKeySearchFn = getReadyMixFolderKey;
      break;
    default:
      if (productLine !== ProductLine.None) {
        recordLog(LogLevel.Warn, `Invalid ProductLine "${productLine}" unable to sort statuses.`);
      }
      break;
  }

  // the backend does not provide statuses in a reliable order
  // therefore we need a keySearchFn in order to sort statuses meaningfully
  if (!folderKeySearchFn) {
    return [];
  }

  workflow.forEach(({ key }) => {
    const matchingStatuses = statuses.filter(status => {
      const statusFolderKey = folderKeySearchFn(status.status, folderSource);
      return statusFolderKey === key;
    });
    const sortedMatchingStatuses = matchingStatuses.sort(sortStatuses);
    orderedData.push(...sortedMatchingStatuses);
  });

  return orderedData;
}

/**
 * This function determines the associated workflow and returns the statuses
 * ordered based on the workflow's steps
 * @param statuses the list of statuses
 * @param isExternal whether the statuses represent an internal or external driver
 * @param productLine the target product line
 * @returns an array containing the statuses ordered by the matched workflow or an empty array
 * when a workflow match can not be determined
 */
export function getLatestWorkflowStatus(
  statuses: StatusEventDto[],
  isExternal: boolean,
  productLine: ProductLine | undefined,
): StatusEventDto | undefined {
  if (!productLine) {
    return undefined;
  }

  const workflowProductLine = productLine as WorkFlowProductLines;
  let workflow = statusWorkflowByProductLineInternal[workflowProductLine];

  const source =
    isExternal === true
      ? FOLDER_LIST_SOURCE_KEY.EXTERNAL_TRUCKS
      : FOLDER_LIST_SOURCE_KEY.INTERNAL_TRUCKS;
  if (source === FOLDER_LIST_SOURCE_KEY.EXTERNAL_TRUCKS) {
    workflow = statusWorkflowByProductLineExternal[workflowProductLine];
  }

  const orderedStatuses = sortStatusesByWorkflow(workflow, workflowProductLine, statuses, source);
  return orderedStatuses.at(-1);
}

export function filterMarkersBasedOnWorkflow(
  markers: MapMarker[],
  productLine: WorkFlowProductLines,
): MapMarker[] {
  const workflowMatches: MapMarker[] = [];
  const includeInternalWorkflow = markers.find(
    marker => determineMarkerPosition(marker)?.isExternal !== true,
  );
  const includeExternalWorkflow = markers.find(
    marker => determineMarkerPosition(marker)?.isExternal === true,
  );
  const internalWorkflow = includeInternalWorkflow
    ? statusWorkflowByProductLineInternal[productLine]
    : null;
  const externalWorkflow = includeExternalWorkflow
    ? statusWorkflowByProductLineExternal[productLine]
    : null;
  if (!internalWorkflow && !externalWorkflow) {
    return [];
  }
  markers.forEach(marker => {
    const { status = constants.STATUS_IDS.UNKNOWN } = marker.data;
    const position = determineMarkerPosition(marker);
    if (!position) {
      return;
    }

    const { driver = { isExternal: false }, productLine = ProductLine.None } = position;
    const hasWorkflowMatch = statusPermittedByAssociatedWorkflow(
      status,
      driver.isExternal,
      productLine as WorkFlowProductLines,
    );
    if (hasWorkflowMatch) {
      workflowMatches.push(marker);
    }
  });

  return workflowMatches;
}

export function statusPermittedByAssociatedWorkflow(
  status: string,
  isExternal: boolean,
  productLine: WorkFlowProductLines,
): boolean {
  if (!status || !productLine) {
    return false;
  }
  const source =
    isExternal !== true
      ? FOLDER_LIST_SOURCE_KEY.INTERNAL_TRUCKS
      : FOLDER_LIST_SOURCE_KEY.EXTERNAL_TRUCKS;
  const matches = hasWorkflowFolderMatch(productLine, status, source);
  return matches;
}

const STATUSES_FOR_PREDICTIVE_ROUTE: ReadonlyArray<string> = [
  'Ticketed',
  'Loading',
  STATUS_DELIVERY_START,
  'ToJob',
];
const STATUSES_FOR_ETA_CALCULATION: ReadonlyArray<string> = [
  ...STATUSES_FOR_PREDICTIVE_ROUTE,
  'Returning',
];

export function statusAllowsETACalculation(status: string) {
  const match = STATUSES_FOR_ETA_CALCULATION.includes(status);
  return match;
}

export function statusAllowsPredictiveRoute(status: string) {
  const match = STATUSES_FOR_PREDICTIVE_ROUTE.includes(status);
  return match;
}
